import { safeParseResponse } from './adapter';
import { UIBlock } from './types';

/**
 * Serialize UIBlocks back to markdown, for "Copy as markdown".
 *
 * Agent responses are either free-form markdown (already a single text block) or structured JSON that the
 * adapter expands into typed blocks. Copying the raw `content` would paste JSON for the latter, so every
 * response is routed through the same adapter the renderer uses and each block is written out as the
 * markdown closest to what the user sees. Interactive `actions` blocks have no textual meaning and are
 * dropped.
 */

const TREND_ARROWS: Record<string, string> = { up: '▲', down: '▼' };

/**
 * Coerce an agent-supplied value to text. Blocks from a `blocks` payload are only shape-checked by the
 * adapter, so a field typed as a string can arrive as an object, number or undefined at runtime; without
 * this those would copy as "[object Object]" or "undefined".
 */
function toText(value: unknown): string {
  return value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
}

/** A table cell must stay on one line and must not terminate the cell early. */
function escapeCell(value: unknown): string {
  return toText(value).replace(/\|/g, '\\|').replace(/\r?\n/g, ' ');
}

function columnLabel(col: string | { name?: string; key?: string }): string {
  if (typeof col === 'string') return col;
  return col?.name || col?.key || '';
}

function columnKey(col: string | { name?: string; key?: string }): string {
  if (typeof col === 'string') return col;
  return col?.key || col?.name || '';
}

function markdownTable(headers: string[], rows: unknown[][]): string {
  const head = `| ${headers.map(escapeCell).join(' | ')} |`;
  const divider = `| ${headers.map(() => '---').join(' | ')} |`;
  const body = rows.map((row) => `| ${row.map(escapeCell).join(' | ')} |`);
  return [head, divider, ...body].join('\n');
}

/**
 * A fence must be longer than any backtick run inside the content, otherwise content containing ``` would
 * close the block early.
 */
function fenced(content: string, language = ''): string {
  const longestRun = (content.match(/`+/g) || []).reduce((max, run) => Math.max(max, run.length), 0);
  const fence = '`'.repeat(Math.max(3, longestRun + 1));
  return `${fence}${language}\n${content}\n${fence}`;
}

function quote(text: string): string {
  return text
    .split('\n')
    .map((line) => (line ? `> ${line}` : '>'))
    .join('\n');
}

function blockToMarkdown(block: UIBlock): string | null {
  switch (block.type) {
    case 'text':
      return block.text;
    case 'metric': {
      const arrow = block.trend ? TREND_ARROWS[block.trend] : undefined;
      return `**${toText(block.label)}:** ${toText(block.value)}${arrow ? ` ${arrow}` : ''}`;
    }
    case 'table': {
      const headers = block.columns.map(columnLabel);
      const rows = block.rows.map((row) =>
        Array.isArray(row) ? row : block.columns.map((col) => (row as Record<string, unknown>)[columnKey(col)] ?? '')
      );
      return markdownTable(headers, rows);
    }
    case 'chart': {
      // The rendered chart has no markdown equivalent; its underlying data as a table is the faithful copy.
      const headers = [block.xKey, ...block.yKeys];
      const rows = block.data.map((point) => headers.map((key) => point?.[key] ?? ''));
      return markdownTable(headers, rows);
    }
    case 'image':
      return `![${block.alt || ''}](${block.url})`;
    case 'code':
      return fenced(block.content, block.language && block.language !== 'text' ? block.language : '');
    case 'callout': {
      const heading = block.title || block.level.charAt(0).toUpperCase() + block.level.slice(1);
      return quote(`**${heading}**\n\n${block.text}`);
    }
    case 'timeline':
      return block.items
        .map((item) => `- **${item.time}** ${item.label}${item.description ? `: ${item.description}` : ''}`)
        .join('\n');
    case 'json': {
      const json = typeof block.data === 'string' ? block.data : JSON.stringify(block.data, null, 2);
      return fenced(json ?? '', 'json');
    }
    case 'actions':
      return null;
    default:
      return null;
  }
}

export function blocksToMarkdown(blocks: UIBlock[]): string {
  return (
    blocks
      .map(blockToMarkdown)
      .filter((part): part is string => !!part && part.trim().length > 0)
      // Trailing whitespace only: leading indentation is significant in markdown (e.g. indented code).
      .map((part) => part.replace(/\s+$/, ''))
      .join('\n\n')
      .replace(/^\n+/, '')
  );
}

/** Markdown for an agent message's raw `content`, mirroring what the chat renders. */
export function responseToMarkdown(content: string): string {
  return blocksToMarkdown(safeParseResponse(content));
}
